import type { Logger, LogContext } from '../types';

/**
 * Console logger implementation for development/fallback
 * Falls back to console when Sentry is not available
 */
export class ConsoleLogger implements Logger {
  private formatMessage(level: string, message: string, context?: LogContext): void {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${level.toUpperCase()}] ${message}`;

    if (context && Object.keys(context).length > 0) {
      console.log(logMessage, context);
    } else {
      console.log(logMessage);
    }
  }

  trace(message: string, context?: LogContext): void {
    this.formatMessage('trace', message, context);
  }

  debug(message: string, context?: LogContext): void {
    this.formatMessage('debug', message, context);
  }

  info(message: string, context?: LogContext): void {
    this.formatMessage('info', message, context);
  }

  warn(message: string, context?: LogContext): void {
    console.warn(`[WARN] ${message}`, context || '');
  }

  error(message: string, context?: LogContext): void {
    console.error(`[ERROR] ${message}`, context || '');
  }

  fatal(message: string, context?: LogContext): void {
    console.error(`[FATAL] ${message}`, context || '');
  }

  captureException(error: Error, context?: LogContext): void {
    console.error(`[EXCEPTION] ${error.name}: ${error.message}`, {
      stack: error.stack,
      ...context,
    });
  }

  /**
   * Template literal function for structured logging
   * Falls back to simple string interpolation
   */
  fmt(strings: TemplateStringsArray, ...values: unknown[]): string {
    return strings.reduce((result, str, i) => {
      return result + str + (values[i] ?? '');
    }, '');
  }
}

